import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { from, of, switchMap } from 'rxjs';
import { ElxKeycloakService } from '../services/keycloak.service';

export const apiInterceptor: HttpInterceptorFn = (req, next) => {
  const keycloak = inject(ElxKeycloakService);

  const getCookie = (name: string): string | null => {
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)'),
    );
    return match ? decodeURIComponent(match[2]) : null;
  };

  const isPublicUrl =
    req.url.includes('/auth/login') ||
    req.url.includes('/auth/notify-login') ||
    req.url.includes('/protocol/openid-connect/token') ||
    req.url.includes('/assets/') ||
    req.url.endsWith('.json');

  const shouldAttachToken = !isPublicUrl;

  const isLogout = req.url.includes('/auth/logout');

  const token$ = shouldAttachToken
    ? from(keycloak.ensureValidToken()).pipe(
        switchMap((token) => {
          if (!token && isLogout) {
            return of(keycloak.restorePersistedTokens()?.token);
          }
          return of(token);
        }),
      )
    : of(undefined);

  return token$.pipe(
    switchMap((token) => {
      const headers: Record<string, string> = {};

      if (
        req.method !== 'GET' &&
        req.method !== 'HEAD' &&
        !(req.body instanceof FormData)
      ) {
        headers['Content-Type'] = 'application/json';
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // CSRF Handling for state-changing requests
      if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(req.method)) {
        const xsrfToken = getCookie('XSRF-TOKEN');
        if (xsrfToken) {
          headers['X-XSRF-TOKEN'] = xsrfToken;
        }
      }

      const modifiedReq = req.clone({
        setHeaders: headers,
        withCredentials: true,
      });
      return next(modifiedReq);
    }),
  );
};
